import React, { useMemo } from "react";

import { Grid } from "@mui/material";
import { Input } from "Components/Form";
import { MilestoneDates } from "Components/Generic/MilestoneDates";
import { formatInTimeZone } from "date-fns-tz";
import { MilestoneType } from "Models/milestone";
import { useTranslation } from "react-i18next";

import { DefaultProps } from "../../../types";
import { SectionTitle } from "../SectionTitle";
import { ViewField } from "../ViewField";
import { ViewModeComponent } from "./components/view-mode";
import { useShipmentContext } from "../../../hooks/useContext";
import { AirFormAirports } from "./components/airport";
import { AirFormGeneral } from "./components/general";
import { AirFormService } from "./components/service";

interface AirFormProps extends DefaultProps {}

export const AirForm = (props: AirFormProps) => {
  const { shipment, milestones, setShipment } = useShipmentContext();
  const hasMarineTraffic = useMemo(() => !!shipment?.marineTrafficId, [shipment]);
  const isShipmentTrackingFetched = useMemo(() => shipment?.fetchedData, [shipment]);

  const { t: translate } = useTranslation();

  const renderMileStones = () => {
    return (
      <React.Fragment>
        <MilestoneDates
          prefixCustomTemplateViewMode={
            <ViewField
              label={translate("loadBy")}
              value={
                shipment?.cargoReadyDate
                  ? formatInTimeZone(new Date(shipment?.cargoReadyDate), "UTC", "yyyy-MM-dd")
                  : ""
              }
            />
          }
          prefixCustomTemplate={
            <Input
              type="date"
              label={translate("loadBy")}
              onChange={(event) =>
                setShipment({
                  ...shipment,
                  cargoReadyDate: event.target.value
                    ? formatInTimeZone(new Date(event.target.value), "UTC", "yyyy-MM-dd")
                    : undefined,
                })
              }
              value={
                shipment?.cargoReadyDate
                  ? formatInTimeZone(new Date(shipment?.cargoReadyDate), "UTC", "yyyy-MM-dd")
                  : ""
              }
            />
          }
          type={MilestoneType.ESTIMATED}
          title={translate(`estimatedDates`)}
          values={shipment?.milestones || []}
          fieldSize={3}
          options={milestones}
          onUpdate={(_, value) => {
            setShipment({ ...shipment, milestones: value ?? undefined });
          }}
          viewOnly={props?.onViewMode}
          viewOnlyComponent={(label: string, value?: string) => (
            <ViewModeComponent onViewMode={props?.onViewMode} formData={{ label, value }} />
          )}
        />

        <MilestoneDates
          type={MilestoneType.ACTUAL}
          title={translate("actualDates")}
          values={shipment?.milestones || []}
          fieldSize={3}
          options={milestones}
          onUpdate={(_, value) => {
            setShipment({ ...shipment, milestones: value ?? undefined });
          }}
          viewOnly={props?.onViewMode}
          viewOnlyComponent={(label: string, value?: string) => (
            <ViewModeComponent onViewMode={props?.onViewMode} formData={{ label, value }} />
          )}
        />
      </React.Fragment>
    );
  };
  const renderDatesFieldByFetchedData = () => {
    return props?.onViewMode ? (
      <React.Fragment>
        <Grid item xs={12}>
          <SectionTitle title={translate("estimatedDates")} />
        </Grid>
        <Grid container item spacing={3}>
          <Grid item xs={12} md={3} lg={3}>
            <ViewField
              label={translate("loadBy")}
              value={
                shipment?.cargoReadyDate
                  ? formatInTimeZone(new Date(shipment?.cargoReadyDate), "UTC", "yyyy-MM-dd")
                  : ""
              }
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3}>
            <ViewField
              label={translate("estimateTimeOfDeparture")}
              value={
                shipment?.shipmentTracking?.originPortPredictiveDeparture
                  ? formatInTimeZone(
                      new Date(shipment?.shipmentTracking?.originPortPredictiveDeparture),
                      "UTC",
                      "yyyy-MM-dd"
                    )
                  : ""
              }
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3}>
            <ViewField
              label={translate("estimateTimeOfArrival")}
              value={
                shipment?.shipmentTracking?.finalPortPredictiveArrivalLast
                  ? formatInTimeZone(
                      new Date(shipment?.shipmentTracking?.finalPortPredictiveArrivalLast),
                      "UTC",
                      "yyyy-MM-dd"
                    )
                  : ""
              }
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <SectionTitle title={translate("actualDates")} />
        </Grid>
        <Grid container item spacing={2}>
          <Grid item xs={12} md={3} lg={3}>
            <ViewField
              label={translate("atDepartureDock")}
              value={
                shipment?.shipmentTracking?.originPortActualDeparture
                  ? formatInTimeZone(
                      new Date(shipment?.shipmentTracking?.originPortActualDeparture),
                      "UTC",
                      "yyyy-MM-dd"
                    )
                  : ""
              }
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3}>
            <ViewField
              label={translate("departureDate")}
              value={
                shipment?.shipmentTracking?.originPortActualDeparture
                  ? formatInTimeZone(
                      new Date(shipment?.shipmentTracking?.originPortActualDeparture),
                      "UTC",
                      "yyyy-MM-dd"
                    )
                  : ""
              }
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3}>
            <ViewField
              label={translate("arrivalAtAod")}
              value={
                shipment?.shipmentTracking?.finalPortActualArrival
                  ? formatInTimeZone(
                      new Date(shipment?.shipmentTracking?.finalPortActualArrival),
                      "UTC",
                      "yyyy-MM-dd"
                    )
                  : ""
              }
            />
          </Grid>
        </Grid>
      </React.Fragment>
    ) : (
      <React.Fragment>
        <Grid item xs={12}>
          <SectionTitle title={translate("estimatedDates")} />
        </Grid>
        <Grid item container direction="row" spacing={3}>
          <Grid item xs={12} md={3} lg={3}>
            <Input
              type="date"
              label={translate("loadBy")}
              onChange={(event) =>
                setShipment({
                  ...shipment,
                  cargoReadyDate: event.target.value
                    ? formatInTimeZone(new Date(event.target.value), "UTC", "yyyy-MM-dd")
                    : undefined,
                })
              }
              value={
                shipment?.cargoReadyDate
                  ? formatInTimeZone(new Date(shipment?.cargoReadyDate), "UTC", "yyyy-MM-dd")
                  : ""
              }
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3}>
            <Input
              type="date"
              disabled={hasMarineTraffic || isShipmentTrackingFetched}
              label={translate("estimateTimeOfDeparture")}
              value={
                shipment?.shipmentTracking?.originPortPredictiveDeparture
                  ? formatInTimeZone(
                      new Date(shipment?.shipmentTracking?.originPortPredictiveDeparture),
                      "UTC",
                      "yyyy-MM-dd"
                    )
                  : ""
              }
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3}>
            <Input
              type="date"
              label={translate("estimateTimeOfArrival")}
              disabled={hasMarineTraffic || isShipmentTrackingFetched}
              value={
                shipment?.shipmentTracking?.finalPortPredictiveArrivalLast
                  ? formatInTimeZone(
                      new Date(shipment?.shipmentTracking?.finalPortPredictiveArrivalLast),
                      "UTC",
                      "yyyy-MM-dd"
                    )
                  : ""
              }
            />
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <SectionTitle title={translate("actualDates")} />
        </Grid>

        <Grid item container direction="row" spacing={3}>
          <Grid item xs={12} md={3} lg={3}>
            <Input
              type="date"
              label={translate("atDepartureDock")}
              disabled={hasMarineTraffic || isShipmentTrackingFetched}
              value={
                shipment?.shipmentTracking?.originPortActualDeparture
                  ? formatInTimeZone(
                      new Date(shipment?.shipmentTracking?.originPortActualDeparture),
                      "UTC",
                      "yyyy-MM-dd"
                    )
                  : ""
              }
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3}>
            <Input
              type="date"
              label={translate("dateOfDeparture")}
              disabled={hasMarineTraffic || isShipmentTrackingFetched}
              value={
                shipment?.shipmentTracking?.originPortActualDeparture
                  ? formatInTimeZone(
                      new Date(shipment?.shipmentTracking?.originPortActualDeparture),
                      "UTC",
                      "yyyy-MM-dd"
                    )
                  : ""
              }
            />
          </Grid>

          <Grid item xs={12} md={3} lg={3}>
            <Input
              type="date"
              label={translate("arrivalAtAod")}
              disabled={hasMarineTraffic || isShipmentTrackingFetched}
              value={
                shipment?.shipmentTracking?.finalPortActualArrival
                  ? formatInTimeZone(
                      new Date(shipment?.shipmentTracking?.finalPortActualArrival),
                      "UTC",
                      "yyyy-MM-dd"
                    )
                  : ""
              }
            />
          </Grid>
        </Grid>
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      <Grid item container direction="column" rowGap={5}>
        <Grid item container direction="column" rowGap={3}>
          <AirFormGeneral {...props} />
        </Grid>

        <Grid item container direction="column" rowGap={3}>
          <AirFormService {...props} />
          <AirFormAirports {...props} />
        </Grid>

        <Grid item container direction="column" rowGap={5}>
          {isShipmentTrackingFetched || shipment?.shipmentTrackingId
            ? renderDatesFieldByFetchedData()
            : renderMileStones()}
        </Grid>
      </Grid>
    </React.Fragment>
  );
};
